/**
 * Parametric buildings (flat, mono-pitch, gable, hip, Thai-style) over rectangular, L, T or U footprints.
 *
 * Building-local plan coordinates (mm): x along the ridge (length), y across (width), +y = FRONT, i.e. the side the
 * front roof plane faces (`azimuthDeg`). Elevation is measured from ground level.
 * Roof planes are drawn on the canvas UNFOLDED (true plane dimensions, like any other roof polygon). Each generated
 * polygon carries `bld = { id, plane }` and its edge 0 is the plane's eave, so the 3D frame is derived from the
 * building parameters and the polygon's own eave edge — moving the polygon on the canvas does not move the roof.
 */
import { z } from 'zod';
import { affineRoofFrame, facingOnCanvas, type RoofFrame } from './frames';
import { envelopeZ, planeZ, roofFaces, unionOutline, type EdgeLabel, type RoofFace, type Wing, type WingRoof } from './roof-planes';
import type { Pt2 } from './sections';
import { add3, mul3, UP, type V3 } from './vec';

export const BUILDING_ROOF_TYPES = ['FLAT', 'MONO', 'GABLE', 'HIP', 'THAI'] as const;
export type BuildingRoofType = (typeof BUILDING_ROOF_TYPES)[number];
export const BUILDING_ROOF_LABEL: Record<BuildingRoofType, string> = {
  FLAT: 'Mái bằng (bê tông)',
  MONO: 'Mái dốc một phía',
  GABLE: 'Mái hai dốc (mái đầu hồi)',
  HIP: 'Mái bốn dốc (mái chóp)',
  THAI: 'Mái Thái (bốn dốc, dốc lớn, mái đua rộng)',
};
/** Thai-style roofs are hip roofs with a steep pitch; these are only the dialog's starting values. */
export const THAI_ROOF_DEFAULTS = { pitchDeg: 35, overhangMm: 600, roofMaterial: 'TILE' as const };

export const BUILDING_FOOTPRINTS = ['RECT', 'L', 'T', 'U'] as const;
export type BuildingFootprint = (typeof BUILDING_FOOTPRINTS)[number];
export const BUILDING_FOOTPRINT_LABEL: Record<BuildingFootprint, string> = {
  RECT: 'Chữ nhật',
  L: 'Chữ L (một cánh)',
  T: 'Chữ T (cánh giữa)',
  U: 'Chữ U (hai cánh hai đầu)',
};
export const ROOF_MATERIALS = ['METAL', 'STANDING_SEAM', 'TILE', 'CONCRETE'] as const;
export const ROOF_MATERIAL_LABEL: Record<(typeof ROOF_MATERIALS)[number], string> = {
  METAL: 'Tôn sóng',
  STANDING_SEAM: 'Tôn đứng (khóa seam)',
  TILE: 'Ngói',
  CONCRETE: 'Bê tông',
};

export const buildingSchema = z
  .object({
    id: z.string().min(1).max(40),
    name: z.string().trim().min(1).max(120),
    roofType: z.enum(BUILDING_ROOF_TYPES),
    lengthMm: z.number().min(1000).max(400_000),
    widthMm: z.number().min(1000).max(300_000),
    eaveHeightMm: z.number().min(0).max(60_000),
    pitchDeg: z.number().min(0).max(60),
    azimuthDeg: z.number().min(0).max(360),
    overhangMm: z.number().min(0).max(3000),
    roofThicknessMm: z.number().min(20).max(1000),
    parapetHeightMm: z.number().min(0).max(3000),
    roofMaterial: z.enum(ROOF_MATERIALS).default('METAL'),
    /** Footprint: main wing (length × width) plus wings perpendicular to it */
    footprint: z.enum(BUILDING_FOOTPRINTS).default('RECT'),
    /** Wing width (along the main ridge) */
    wingWidthMm: z.number().min(1000).max(200_000).default(6000),
    /** How far the wing projects beyond the main wing's front / back wall */
    wingLengthMm: z.number().min(500).max(200_000).default(6000),
    /** L: which end carries the wing */
    wingSide: z.enum(['LEFT', 'RIGHT']).default('RIGHT'),
    /** Wings project towards the front (+y) or the back */
    wingDir: z.enum(['FRONT', 'BACK']).default('FRONT'),
    /** Building centre on the canvas (px) */
    center: z.tuple([z.number().finite(), z.number().finite()]),
    /**
     * Free-form building (edited vertex by vertex / face by face): walls follow `footprint` (canvas px, closed polygon)
     * and the roof is the building's own roof polygons (each plane with its own pitch, facing and eave height) instead
     * of being generated from the parameters above. Null / absent = parametric building.
     */
    free: z
      .object({
        footprint: z.array(z.tuple([z.number().finite(), z.number().finite()])).min(3).max(500),
        /** Target elevation (mm) of every vertex of each roof plane (roof id → per vertex); planes are fitted to them */
        vz: z.record(z.string(), z.array(z.number().finite())).optional(),
      })
      .nullable()
      .optional(),
  })
  .loose();
export type Building = z.infer<typeof buildingSchema>;

export const buildingsDataSchema = z
  .object({ version: z.number().int().min(1), items: z.array(buildingSchema).max(100) })
  .loose();

export interface BuildingPlane {
  index: number;
  role: 'FRONT' | 'BACK' | 'LEFT' | 'RIGHT';
  faceDeg: number;
  pitchDeg: number;
  /** Flattened outline in (a, s) mm: a along the eave from its start, s up-slope from the eave */
  flat: Pt2[];
  /** Plan geometry (building-local, mm) of the eave line (outer roof edge) */
  eaveStart: Pt2;
  eaveDir: Pt2;
  inward: Pt2;
}

const n360 = (d: number) => ((d % 360) + 360) % 360;
const rad = (d: number) => (d * Math.PI) / 180;

/** Outer roof outline (with overhang) & slope length. */
function dims(b: Building) {
  const L = b.lengthMm + 2 * b.overhangMm;
  const W = b.widthMm + 2 * b.overhangMm;
  const th = b.roofType === 'FLAT' ? 0 : rad(b.pitchDeg);
  return { L, W, th };
}

/** Elevation (mm) of the roof top at the outer eave of a plane. */
function eaveTopMm(b: Building) {
  const { th } = dims(b);
  return b.eaveHeightMm - b.overhangMm * Math.tan(th);
}

/** Legacy single-wing planes in (a, s) for UNFOLDED drawings (historical canvas). Main wing only. */
export function buildingPlanes(b: Building): BuildingPlane[] {
  const { L, W, th } = dims(b);
  const cos = Math.cos(th);
  const az = b.azimuthDeg;
  const p = b.roofType === 'FLAT' ? 0 : b.pitchDeg;
  const front = (flat: Pt2[]): BuildingPlane => ({
    index: 0,
    role: 'FRONT',
    faceDeg: n360(az),
    pitchDeg: p,
    flat,
    eaveStart: [-L / 2, W / 2],
    eaveDir: [1, 0],
    inward: [0, -1],
  });
  if (b.roofType === 'FLAT' || b.roofType === 'MONO') {
    const d = b.roofType === 'FLAT' ? W : W / cos;
    return [
      front([
        [0, 0],
        [L, 0],
        [L, d],
        [0, d],
      ]),
    ];
  }
  const d = W / 2 / cos;
  const back = (flat: Pt2[]): BuildingPlane => ({
    index: 1,
    role: 'BACK',
    faceDeg: n360(az + 180),
    pitchDeg: p,
    flat,
    eaveStart: [L / 2, -W / 2],
    eaveDir: [-1, 0],
    inward: [0, 1],
  });
  if (b.roofType === 'GABLE') {
    const rect: Pt2[] = [
      [0, 0],
      [L, 0],
      [L, d],
      [0, d],
    ];
    return [front(rect), back(rect.map((q) => [q[0], q[1]] as Pt2))];
  }
  // HIP: equal pitches ⇒ hips at 45° in plan; ridge length L − W (pyramid when L = W).
  const r0 = W / 2;
  const trap: Pt2[] = [
    [0, 0],
    [L, 0],
    [L - r0, d],
    [r0, d],
  ];
  const tri: Pt2[] = [
    [0, 0],
    [W, 0],
    [W / 2, d],
  ];
  return [
    front(trap),
    back(trap.map((q) => [q[0], q[1]] as Pt2)),
    {
      index: 2,
      role: 'LEFT',
      faceDeg: n360(az - 90),
      pitchDeg: p,
      flat: tri,
      eaveStart: [-L / 2, W / 2],
      eaveDir: [0, -1],
      inward: [1, 0],
    },
    {
      index: 3,
      role: 'RIGHT',
      faceDeg: n360(az + 90),
      pitchDeg: p,
      flat: tri.map((q) => [q[0], q[1]] as Pt2),
      eaveStart: [L / 2, -W / 2],
      eaveDir: [0, 1],
      inward: [-1, 0],
    },
  ];
}

export interface BuildingIssue {
  severity: 'error' | 'warning';
  message: string;
}

export function validateBuilding(b: Building): BuildingIssue[] {
  const out: BuildingIssue[] = [];
  const hip = b.roofType === 'HIP' || b.roofType === 'THAI';
  const multi = (b.footprint ?? 'RECT') !== 'RECT';
  if (hip && b.lengthMm < b.widthMm)
    out.push({
      severity: 'error',
      message: 'Mái bốn dốc: chiều dài phải ≥ chiều rộng (đổi hướng nhà nếu cần).',
    });
  if (b.roofType !== 'FLAT' && b.pitchDeg <= 0)
    out.push({ severity: 'error', message: 'Mái dốc cần độ dốc lớn hơn 0°.' });
  if (b.roofType === 'FLAT' && b.overhangMm > 0 && b.parapetHeightMm > 0)
    out.push({ severity: 'warning', message: 'Mái bằng có tường chắn mái thường không có phần mái đua.' });
  if (b.roofType !== 'FLAT' && b.parapetHeightMm > 0)
    out.push({ severity: 'warning', message: 'Tường chắn mái chỉ được dựng cho mái bằng.' });
  if (b.roofType === 'THAI' && b.pitchDeg < 25)
    out.push({ severity: 'warning', message: 'Mái Thái thường dốc 30–45° — kiểm tra lại độ dốc.' });
  if (multi) {
    const w2 = b.wingWidthMm;
    const span = b.widthMm + b.wingLengthMm;
    if (b.roofType === 'MONO')
      out.push({ severity: 'error', message: 'Nhà chữ L / T / U: chỉ hỗ trợ mái bằng, hai dốc, bốn dốc hoặc mái Thái.' });
    if ((b.footprint === 'U' ? 2 * w2 : w2) > b.lengthMm)
      out.push({ severity: 'error', message: 'Cánh nhà rộng hơn chiều dài nhà chính.' });
    if (hip && span < w2)
      out.push({ severity: 'error', message: 'Mái bốn dốc ở cánh: chiều dài cánh (rộng nhà + phần nhô) phải ≥ bề rộng cánh.' });
  }
  return out;
}

/** Ridge elevation (mm): highest point of the roof. */
export function ridgeHeightMm(b: Building): number {
  if (b.roofType === 'FLAT') return b.eaveHeightMm;
  let top = b.eaveHeightMm;
  for (const f of buildingRoofFaces(b)) for (const q of f.poly.pts) top = Math.max(top, planeZ(f.def, q));
  return top;
}

const wingRoofOf = (b: Building): WingRoof => (b.roofType === 'THAI' ? 'HIP' : b.roofType);

/** Wing rectangles (building-local plan mm): the main wing first; `ov` expands each by the roof overhang. */
export function buildingWings(b: Building, ov = 0): Wing[] {
  const L = b.lengthMm;
  const W = b.widthMm;
  const main: Wing = { x0: -L / 2 - ov, x1: L / 2 + ov, y0: -W / 2 - ov, y1: W / 2 + ov, ridge: 'X' };
  const fp = b.footprint ?? 'RECT';
  if (fp === 'RECT') return [main];
  const w2 = Math.min(b.wingWidthMm ?? 6000, L);
  const e = b.wingLengthMm ?? 6000;
  const [y0, y1] = (b.wingDir ?? 'FRONT') === 'FRONT' ? [-W / 2 - ov, W / 2 + e + ov] : [-W / 2 - e - ov, W / 2 + ov];
  const wing = (xc: number): Wing => ({ x0: xc - w2 / 2 - ov, x1: xc + w2 / 2 + ov, y0, y1, ridge: 'Y' });
  if (fp === 'T') return [main, wing(0)];
  if (fp === 'U') return [main, wing(-L / 2 + w2 / 2), wing(L / 2 - w2 / 2)];
  return [main, wing((b.wingSide ?? 'RIGHT') === 'RIGHT' ? L / 2 - w2 / 2 : -L / 2 + w2 / 2)];
}

/** Visible roof planes (building-local plan mm, labelled edges, plane equations). */
export function buildingRoofFaces(b: Building): RoofFace[] {
  const { th } = dims(b);
  return roofFaces(
    buildingWings(b, b.overhangMm),
    wingRoofOf(b),
    b.roofType === 'FLAT' ? 0 : Math.tan(th),
    eaveTopMm(b),
    b.roofType === 'FLAT' && b.parapetHeightMm > 0,
  );
}

/** Compass azimuth of a down-slope direction given in building-local plan coordinates. */
const faceAzimuth = (b: Building, d: Pt2) => n360(b.azimuthDeg + (Math.atan2(d[0], d[1]) * 180) / Math.PI);

/** Drawing-local (unfolded) layout of a plane: (a, s) mm → (x, y) mm, +y = front. */
function unfold(b: Building, pl: BuildingPlane): (q: Pt2) => Pt2 {
  const { L, W, th } = dims(b);
  const gap = Math.max(1000, 0.08 * W);
  const d = b.roofType === 'FLAT' ? W : b.roofType === 'MONO' ? W / Math.cos(th) : W / 2 / Math.cos(th);
  if (b.roofType === 'FLAT' || b.roofType === 'MONO') return ([a, s]) => [-L / 2 + a, d / 2 - s];
  switch (pl.role) {
    case 'FRONT':
      return ([a, s]) => [-L / 2 + a, d - s];
    case 'BACK':
      return ([a, s]) => [L / 2 - a, -d + s];
    case 'LEFT':
      return ([a, s]) => [-L / 2 - gap - d + s, W / 2 - a];
    case 'RIGHT':
    default:
      return ([a, s]) => [L / 2 + gap + d - s, -W / 2 + a];
  }
}

/** Drawing basis: building-local x / y (mm) → canvas px offsets. */
function canvasBasis(b: Building, upBearingDeg: number, pxPerMm: number) {
  const t = rad(b.azimuthDeg - upBearingDeg);
  const X: Pt2 = [Math.cos(t) * pxPerMm, Math.sin(t) * pxPerMm];
  const Y: Pt2 = [Math.sin(t) * pxPerMm, -Math.cos(t) * pxPerMm];
  return (x: number, y: number): Pt2 => [
    b.center[0] + x * X[0] + y * Y[0],
    b.center[1] + x * X[1] + y * Y[1],
  ];
}

export interface BuildingPolygon {
  points: Pt2[];
  face: number;
  pitch: number;
  bld: { id: string; plane: number };
}

/** Roof polygons to add to the drawing (unfolded planes; edge 0 = eave). */
export function buildingDrawing(b: Building, pxPerMm: number, upBearingDeg: number): BuildingPolygon[] {
  const toCanvas = canvasBasis(b, upBearingDeg, pxPerMm);
  return buildingPlanes(b).map((pl) => {
    const f = unfold(b, pl);
    return {
      points: pl.flat.map((q) => toCanvas(...f(q))),
      face: Math.round(pl.faceDeg * 1e6) / 1e6,
      pitch: pl.pitchDeg,
      bld: { id: b.id, plane: pl.index },
    };
  });
}

/** Building-local plan (mm) + elevation (mm) → world (m), scene centred on `center` (m). */
export function buildingToWorld(
  b: Building,
  pxPerMm: number,
  upBearingDeg: number,
  center: [number, number],
) {
  const t = rad(b.azimuthDeg - upBearingDeg);
  const cx = b.center[0] / pxPerMm / 1000 - center[0];
  const cz = b.center[1] / pxPerMm / 1000 - center[1];
  return (x: number, y: number, elev: number): V3 => [
    cx + (x * Math.cos(t) + y * Math.sin(t)) / 1000,
    elev / 1000,
    cz + (x * Math.sin(t) - y * Math.cos(t)) / 1000,
  ];
}


/**
 * 3D frame of a building plane for a polygon drawn on the canvas.
 * (a, s) come from the polygon's own edge 0 (eave), so edits that keep the eave edge keep the mapping.
 */
export function buildingPlaneFrame(
  b: Building,
  planeIndex: number,
  polygon: readonly Pt2[],
  pxPerMm: number,
  upBearingDeg: number,
  center: [number, number],
): RoofFrame | null {
  const pl = buildingPlanes(b)[planeIndex];
  if (!pl || polygon.length < 3) return null;
  const [p0, p1] = [polygon[0]!, polygon[1]!];
  const el = Math.hypot(p1[0] - p0[0], p1[1] - p0[1]);
  if (!(el > 0)) return null;
  const e: Pt2 = [(p1[0] - p0[0]) / el, (p1[1] - p0[1]) / el];
  let n: Pt2 = [-e[1], e[0]];
  const cx = polygon.reduce((t, q) => t + q[0], 0) / polygon.length;
  const cy = polygon.reduce((t, q) => t + q[1], 0) / polygon.length;
  if ((cx - p0[0]) * n[0] + (cy - p0[1]) * n[1] < 0) n = [-n[0], -n[1]];
  const th = rad(pl.pitchDeg);
  const cos = Math.cos(th);
  const sin = Math.sin(th);
  const z0 = eaveTopMm(b);
  const w = buildingToWorld(b, pxPerMm, upBearingDeg, center);
  const map = (p: Pt2): V3 => {
    const a = ((p[0] - p0[0]) * e[0] + (p[1] - p0[1]) * e[1]) / pxPerMm;
    const s = ((p[0] - p0[0]) * n[0] + (p[1] - p0[1]) * n[1]) / pxPerMm;
    const x = pl.eaveStart[0] + a * pl.eaveDir[0] + s * cos * pl.inward[0];
    const y = pl.eaveStart[1] + a * pl.eaveDir[1] + s * cos * pl.inward[1];
    return w(x, y, z0 + s * sin);
  };
  const fc = facingOnCanvas(pl.faceDeg, upBearingDeg);
  return affineRoofFrame(map, pl.pitchDeg, [fc[0], 0, fc[1]]);
}

export interface BuildingMesh {
  /** Wall polygons (world, m), each planar & vertical (top follows the roof) */
  walls: V3[][];
  /** Roof plane top outlines (world, m; may be non-convex) with the plane's unit normal */
  roofs: { outline: V3[]; normal: V3; plane: number }[];
  /** Parapet walls as closed polygons (outer faces) — flat roofs only */
  parapets: V3[][];
  /** Horizontal unit vector from each parapet towards the roof inside */
  parapetInward: V3[];
  /** Ridge, hip and valley lines */
  ridges: [V3, V3][];
  thicknessM: number;
  parapetThicknessM: number;
}

/** Solid geometry of the building body & roof for rendering. */
export function buildingMesh(
  b: Building,
  pxPerMm: number,
  upBearingDeg: number,
  center: [number, number],
): BuildingMesh {
  const w = buildingToWorld(b, pxPerMm, upBearingDeg, center);
  const o = w(0, 0, 0);
  const dirW = (d: Pt2): V3 => {
    const q = w(d[0], d[1], 0);
    return [q[0] - o[0], 0, q[2] - o[2]];
  };
  const { th } = dims(b);
  const tan = b.roofType === 'FLAT' ? 0 : Math.tan(th);
  const roofW = wingRoofOf(b);
  const z0 = eaveTopMm(b);
  const wingsOv = buildingWings(b, b.overhangMm);
  const faces = buildingRoofFaces(b);
  const top = (p: Pt2) => envelopeZ(wingsOv, roofW, tan, z0, p) ?? b.eaveHeightMm;

  // Walls along the footprint outline, top following the roof surface (breakpoints where planes change)
  const lines: { n: Pt2; c: number }[] = [];
  const defs = faces.map((f) => f.def);
  for (let i = 0; i < defs.length; i++)
    for (let j = i + 1; j < defs.length; j++) {
      const P = defs[i]!;
      const Q = defs[j]!;
      lines.push({
        n: [P.tan * P.n[0] - Q.tan * Q.n[0], P.tan * P.n[1] - Q.tan * Q.n[1]],
        c: P.z0 + P.tan * P.c - (Q.z0 + Q.tan * Q.c),
      });
    }
  for (const r of wingsOv) lines.push({ n: [1, 0], c: -r.x0 }, { n: [1, 0], c: -r.x1 }, { n: [0, 1], c: -r.y0 }, { n: [0, 1], c: -r.y1 });
  const walls: V3[][] = [];
  for (const loop of unionOutline(buildingWings(b, 0))) {
    for (let i = 0; i < loop.length; i++) {
      const a = loop[i]!;
      const c = loop[(i + 1) % loop.length]!;
      const ts = new Set<number>([0, 1]);
      for (const l of lines) {
        const fa = l.n[0] * a[0] + l.n[1] * a[1] + l.c;
        const fc = l.n[0] * c[0] + l.n[1] * c[1] + l.c;
        if (Math.abs(fa - fc) < 1e-9) continue;
        const t = fa / (fa - fc);
        if (t > 1e-6 && t < 1 - 1e-6) ts.add(Math.round(t * 1e9) / 1e9);
      }
      const at = (t: number): Pt2 => [a[0] + (c[0] - a[0]) * t, a[1] + (c[1] - a[1]) * t];
      const profile = [...ts].sort((x, y) => x - y).map((t) => at(t));
      const poly: V3[] = [w(a[0], a[1], 0), w(c[0], c[1], 0)];
      for (const q of [...profile].reverse()) poly.push(w(q[0], q[1], top(q)));
      walls.push(poly);
    }
  }
  const roofs = faces.map((f) => {
    const d = f.def;
    const sin = Math.sin(Math.atan(d.tan));
    const cos = Math.cos(Math.atan(d.tan));
    const down = dirW([-d.n[0], -d.n[1]]);
    const outline = f.poly.pts.map((q) => w(q[0], q[1], planeZ(d, q)));
    const normal: V3 = [down[0] * sin, cos, down[2] * sin];
    return { outline, normal, plane: f.plane };
  });
  const ridges: [V3, V3][] = [];
  const seen = new Set<string>();
  const rk = (q: Pt2) => `${Math.round(q[0])},${Math.round(q[1])}`;
  const lineKinds: EdgeLabel[] = ['RIDGE', 'HIP', 'VALLEY'];
  for (const f of faces)
    f.poly.pts.forEach((q, i) => {
      if (!lineKinds.includes(f.poly.labels[i]!)) return;
      const r = f.poly.pts[(i + 1) % f.poly.pts.length]!;
      const k = [rk(q), rk(r)].sort().join('|');
      if (seen.has(k)) return;
      seen.add(k);
      ridges.push([w(q[0], q[1], planeZ(f.def, q)), w(r[0], r[1], planeZ(f.def, r))]);
    });
  const parapets: V3[][] = [];
  const parapetInward: V3[] = [];
  if (b.roofType === 'FLAT' && b.parapetHeightMm > 0) {
    const eave = b.eaveHeightMm;
    for (const loop of unionOutline(wingsOv)) {
      const sgn = Math.sign(loop.reduce((t, q, i) => t + q[0] * loop[(i + 1) % loop.length]![1] - loop[(i + 1) % loop.length]![0] * q[1], 0)) || 1;
      for (let i = 0; i < loop.length; i++) {
        const a = loop[i]!;
        const c = loop[(i + 1) % loop.length]!;
        parapets.push([
          w(a[0], a[1], eave),
          w(c[0], c[1], eave),
          w(c[0], c[1], eave + b.parapetHeightMm),
          w(a[0], a[1], eave + b.parapetHeightMm),
        ]);
        const l = Math.hypot(c[0] - a[0], c[1] - a[1]) || 1;
        // Left normal of a counter-clockwise loop points inside
        const inn: Pt2 = [(-(c[1] - a[1]) / l) * sgn, ((c[0] - a[0]) / l) * sgn];
        const v = dirW(inn);
        const lv = Math.hypot(v[0], v[2]) || 1;
        parapetInward.push([v[0] / lv, 0, v[2] / lv]);
      }
    }
  }
  return {
    walls,
    roofs,
    parapets,
    parapetInward,
    ridges,
    thicknessM: b.roofThicknessMm / 1000,
    parapetThicknessM: 0.2,
  };
}

/** World offset helper used by annotations. */
export const liftM = (p: V3, m: number): V3 => add3(p, mul3(UP, m));

export interface BuildingPlanRoof extends BuildingPolygon {
  /** Roof-top elevation at the plane's lowest (down-slope) point (m) */
  heightM: number;
  /** Edge kind per edge i (points[i] → points[i + 1]): eave, ridge, hip, valley, rake, parapet */
  edges: EdgeLabel[];
}

/**
 * Roof planes as true PLAN footprints (top view) for plan-based drawings: each visible plane's outline in canvas px,
 * its facing, pitch, eave elevation and edge kinds. Works for every footprint (rectangle, L, T, U).
 */
export function buildingPlanRoofs(b: Building, pxPerMm: number, upBearingDeg: number): BuildingPlanRoof[] {
  const toCanvas = canvasBasis(b, upBearingDeg, pxPerMm);
  return buildingRoofFaces(b).map((f) => {
    const d = f.def;
    const down: Pt2 = [-d.n[0], -d.n[1]];
    let low = f.poly.pts[0]!;
    for (const q of f.poly.pts) if (q[0] * down[0] + q[1] * down[1] > low[0] * down[0] + low[1] * down[1]) low = q;
    const pitch = b.roofType === 'FLAT' ? 0 : b.pitchDeg;
    return {
      points: f.poly.pts.map((q) => toCanvas(q[0], q[1])),
      face: Math.round((pitch > 0 ? faceAzimuth(b, down) : n360(b.azimuthDeg)) * 1e6) / 1e6,
      pitch,
      heightM: planeZ(d, low) / 1000,
      edges: [...f.poly.labels],
      bld: { id: b.id, plane: f.plane },
    };
  });
}

const pipXY = (p: Pt2, poly: readonly Pt2[]) => {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const a = poly[i]!;
    const b = poly[j]!;
    if (a[1] > p[1] !== b[1] > p[1] && p[0] < ((b[0] - a[0]) * (p[1] - a[1])) / (b[1] - a[1] || 1e-12) + a[0]) inside = !inside;
  }
  return inside;
};

/** A roof plane of a free-form building: its plan polygon (canvas px) and 3D frame. */
export interface FreeRoofPlane {
  plane: number;
  points: readonly Pt2[];
  frame: RoofFrame;
}

/**
 * Solid geometry of a FREE-FORM building: vertical walls along `b.free.footprint` from the ground up to the underside
 * of the roof plane above (eave height where no plane covers the wall), the building's own roof planes, and the
 * ridge / hip / valley lines where two planes share an edge.
 */
export function freeBuildingMesh(
  b: Building,
  planes: readonly FreeRoofPlane[],
  pxPerMm: number,
  center: [number, number],
): BuildingMesh {
  const k = 1 / pxPerMm / 1000;
  const fp = b.free?.footprint ?? [];
  const th = b.roofThicknessMm / 1000;
  const ground = (q: Pt2, y: number): V3 => [q[0] * k - center[0], y, q[1] * k - center[1]];
  const topAt = (q: Pt2, inside: Pt2): number => {
    let best = -Infinity;
    for (const pl of planes) if (pipXY(inside, pl.points)) best = Math.max(best, pl.frame.map(q)[1] - th);
    return best > -Infinity ? best : b.eaveHeightMm / 1000;
  };
  const walls: V3[][] = [];
  for (let i = 0; i < fp.length; i++) {
    const a = fp[i]!;
    const c = fp[(i + 1) % fp.length]!;
    const dx = c[0] - a[0];
    const dy = c[1] - a[1];
    const l2 = dx * dx + dy * dy;
    if (!l2) continue;
    // Breakpoints where the wall crosses a roof polygon edge (the roof above changes plane there)
    const ts = new Set<number>([0, 1]);
    for (const pl of planes)
      pl.points.forEach((p, n) => {
        const q = pl.points[(n + 1) % pl.points.length]!;
        const ex = q[0] - p[0];
        const ey = q[1] - p[1];
        const den = dx * ey - dy * ex;
        if (Math.abs(den) < 1e-12) return;
        const t = ((p[0] - a[0]) * ey - (p[1] - a[1]) * ex) / den;
        const u = ((p[0] - a[0]) * dy - (p[1] - a[1]) * dx) / den;
        if (t > 1e-6 && t < 1 - 1e-6 && u >= -1e-6 && u <= 1 + 1e-6) ts.add(t);
      });
    const sorted = [...ts].sort((x, y) => x - y);
    const at = (t: number): Pt2 => [a[0] + dx * t, a[1] + dy * t];
    // Top profile: each span takes the plane covering its midpoint (steps where planes differ in height)
    const profile: V3[] = [];
    for (let s = 0; s + 1 < sorted.length; s++) {
      const t0 = sorted[s]!;
      const t1 = sorted[s + 1]!;
      const mid = at((t0 + t1) / 2);
      profile.push(ground(at(t0), topAt(at(t0), mid)), ground(at(t1), topAt(at(t1), mid)));
    }
    const poly: V3[] = [ground(a, 0), ground(c, 0)];
    for (const q of [...profile].reverse()) {
      const last = poly[poly.length - 1]!;
      if (Math.abs(last[0] - q[0]) + Math.abs(last[1] - q[1]) + Math.abs(last[2] - q[2]) > 1e-9) poly.push(q);
    }
    walls.push(poly);
  }
  const roofs = planes.map((pl) => ({ outline: pl.points.map((q) => pl.frame.map(q)), normal: pl.frame.normal, plane: pl.plane }));
  // Shared edges between two planes: ridge / hip / valley lines
  const ridges: [V3, V3][] = [];
  const tol = 5 * pxPerMm;
  const same = (p: Pt2, q: Pt2) => Math.abs(p[0] - q[0]) <= tol && Math.abs(p[1] - q[1]) <= tol;
  planes.forEach((A, i) =>
    A.points.forEach((p, n) => {
      const q = A.points[(n + 1) % A.points.length]!;
      for (const B of planes.slice(i + 1))
        B.points.forEach((r, m) => {
          const s = B.points[(m + 1) % B.points.length]!;
          if ((same(p, s) && same(q, r)) || (same(p, r) && same(q, s))) ridges.push([A.frame.map(p), A.frame.map(q)]);
        });
    }),
  );
  return { walls, roofs, parapets: [], parapetInward: [], ridges, thicknessM: th, parapetThicknessM: 0.2 };
}
